/**
 * FR-018.7 — the dry-run preview and the job outcome as plain sentences
 * (TENANT_CONFIGURATION_UI.md §5 section 4, §6.3 warning table).
 *
 * Counts come from the preview's top-level `counts` (the whole batch); warnings from the top
 * level and from every item. `rematerialise` holds materialiser names — it only decides whether
 * the recalculation sentence appears; the names are never printed.
 */
import type { PreviewResponse, PreviewWarning } from '../../../../services/sourcesService';

export type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

/** Warnings that make Confirm impossible (spec §6.3 "blocks"). */
export const BLOCKING_CODES: ReadonlySet<string> = new Set([
  'overlaps_item',
  'target_not_live',
  'shadowed_by_field_span',
  'tenant_job_in_flight',
  'shadows_field_row',
  // A planner code the farmer preview lifts (final review I2): the move has nowhere to land.
  'target_field_not_live',
]);

const P = 'farm.sources.preview';
const O = 'farm.sources.outcome';

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

function plural(t: Translate, key: string, n: number, one: string, other: string): string {
  return t(key, n === 1 ? one : other, { count: n, n: fmt(n) });
}

/** One warning as a sentence; a code without copy falls back to `warnings.unknown` naming it. */
export function warningSentence(w: PreviewWarning, t: Translate, ctx: { noun: string }): string {
  const seasons = Array.isArray(w.seasons) ? w.seasons.filter((s) => typeof s === 'number') : [];
  const params: Record<string, unknown> = {
    noun: ctx.noun,
    code: w.code,
    index: w.index,
    job_status: w.job_status,
    n: w.count,
    system: w.source_system,
  };
  if (seasons.length) {
    params.from = Math.min(...seasons);
    params.to = Math.max(...seasons);
  }
  const fallback = t('farm.sources.warnings.unknown', 'Hinweis: {{code}}', { code: w.code });
  return t(`farm.sources.warnings.${w.code}`, fallback, params);
}

function allWarnings(preview: PreviewResponse): PreviewWarning[] {
  const seen = new Set<string>();
  const out: PreviewWarning[] = [];
  const add = (w: PreviewWarning | undefined) => {
    if (!w || typeof w.code !== 'string') return;
    const key = `${w.code}|${w.index ?? ''}|${(w.seasons ?? []).join(',')}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(w);
  };
  for (const item of preview.items ?? []) (item.warnings ?? []).forEach(add);
  (preview.warnings ?? []).forEach(add);
  return out;
}

export function previewSentences(
  preview: PreviewResponse,
  t: Translate,
  ctx: { noun: string; targetName?: string },
): { sentences: string[]; blocking: string[]; noop: boolean } {
  const c = preview.counts ?? {};
  const cycles = num(c.cycles);
  const picks = num(c.picks);
  const workingTimes = num(c.working_times);
  const sentences: string[] = [];

  const records: string[] = [];
  if (picks) records.push(plural(t, `${P}.picks`, picks, '{{n}} Pflückleistung', '{{n}} Pflückleistungen'));
  if (workingTimes) records.push(plural(t, `${P}.working_times`, workingTimes, '{{n}} Arbeitszeit', '{{n}} Arbeitszeiten'));
  const parts = records.join(` ${t(`${P}.and`, 'und')} `);
  if (cycles) {
    const head = plural(t, `${P}.cycles`, cycles, '{{n}} Kultur wechselt', '{{n}} Kulturen wechseln');
    sentences.push(parts ? `${head}, ${t(`${P}.with`, 'mit {{parts}}', { parts })}.` : `${head}.`);
  } else if (parts) {
    sentences.push(t(`${P}.recordsMove`, '{{parts}} wechseln.', { parts }));
  }
  const simple: Array<[string, string, string]> = [
    ['operations_remint', '{{n}} Vorgang wird neu zugeordnet.', '{{n}} Vorgänge werden neu zugeordnet.'],
    [
      'operations_duplicate',
      '{{n}} Vorgang betrifft beide Objekte und wird für jedes übernommen.',
      '{{n}} Vorgänge betreffen beide Objekte und werden für jedes übernommen.',
    ],
    ['cycles_to_mint', '{{n}} Kultur wird neu angelegt.', '{{n}} Kulturen werden neu angelegt.'],
    ['parcels_repointed', '{{n}} Flurstück wird umgehängt.', '{{n}} Flurstücke werden umgehängt.'],
  ];
  for (const [key, one, other] of simple) {
    const n = num(c[key]);
    if (n) sentences.push(plural(t, `${P}.${key}`, n, one, other));
  }
  const nothingMoves = sentences.length === 0;
  if (nothingMoves) sentences.push(t(`${P}.nothingMoves`, 'Es werden keine Datensätze verschoben.'));

  const remat = preview.rematerialise;
  const rematNonEmpty = Array.isArray(remat)
    ? remat.length > 0
    : remat !== null && typeof remat === 'object'
      ? Object.keys(remat).length > 0
      : Boolean(remat);
  // With no record moving there is no „both objects“ — but the figures still change (a product row
  // decides which crop an article's revenue counts toward), so the sentence stays, without the pair.
  if (rematNonEmpty) {
    sentences.push(
      nothingMoves
        ? t(`${P}.rematerialiseOnly`, 'Der Deckungsbeitrag wird neu berechnet.')
        : t(`${P}.rematerialise`, 'Der Deckungsbeitrag beider Objekte wird neu berechnet.'),
    );
  }

  const blocking: string[] = [];
  for (const w of allWarnings(preview)) {
    const s = warningSentence(w, t, ctx);
    (BLOCKING_CODES.has(w.code) ? blocking : sentences).push(s);
  }

  sentences.push(
    ctx.targetName
      ? t(`${P}.keep`, 'Bereits auf {{target}} liegende Datensätze bleiben, nichts wird gelöscht.', { target: ctx.targetName })
      : t(`${P}.keepGeneric`, 'Bereits zugeordnete Datensätze bleiben, nichts wird gelöscht.'),
  );
  sentences.push(t(`${P}.undo`, 'Rückgängig: dieselbe Änderung in die andere Richtung.'));

  return { sentences, blocking, noop: preview.would_be_noop === true };
}

/** A finished job's `outcome` (worker `execute_plan`) as past-tense sentences. */
export function jobOutcomeSentences(outcome: Record<string, unknown> | null | undefined, t: Translate): string[] {
  if (outcome?.already_applied === true) return [t(`${O}.alreadyApplied`, 'Die Änderung war bereits übernommen.')];
  const o = outcome ?? {};
  const moved = o.moved && typeof o.moved === 'object' ? Object.keys(o.moved).length : 0;
  const withheld = Array.isArray(o.withheld) ? o.withheld.length : 0;
  const rows: Array<[number, string, string, string]> = [
    [moved, 'cycles', '{{n}} Kultur hat gewechselt.', '{{n}} Kulturen haben gewechselt.'],
    [num(o.picks_repointed), 'picks', '{{n}} Pflückleistung umgehängt.', '{{n}} Pflückleistungen umgehängt.'],
    [num(o.working_times_repointed), 'working_times', '{{n}} Arbeitszeit umgehängt.', '{{n}} Arbeitszeiten umgehängt.'],
    [num(o.operations_reminted), 'operations', '{{n}} Vorgang neu zugeordnet.', '{{n}} Vorgänge neu zugeordnet.'],
    [num(o.minted_cycles), 'minted', '{{n}} Kultur neu angelegt.', '{{n}} Kulturen neu angelegt.'],
    [num(o.parcels_repointed), 'parcels', '{{n}} Flurstück umgehängt.', '{{n}} Flurstücke umgehängt.'],
    [
      withheld,
      'withheld',
      '{{n}} Einheit wurde zurückgehalten und bleibt, wo sie ist.',
      '{{n}} Einheiten wurden zurückgehalten und bleiben, wo sie sind.',
    ],
  ];
  const out = rows.filter(([n]) => n > 0).map(([n, key, one, other]) => plural(t, `${O}.${key}`, n, one, other));
  return out.length ? out : [t(`${O}.nothing`, 'Es musste nichts verschoben werden.')];
}
